import { useAuth } from '@/hooks';
import { useEntity } from '@/contexts/EntityContext';
import { useEffect, useRef, useState, useMemo } from 'react';
import { Box } from '@mui/material';

interface IFrameAppShellProps {
  iframeUrl: string;
  title: string;
}

/**
 * IFrameAppShell component that embeds an external application in an iframe
 * and securely passes authentication tokens via postMessage.
 *
 * Handles navigation between different iframe apps by ensuring postMessage
 * is only sent to the correct iframe after it has fully loaded.
 */
export function IFrameAppShell({ iframeUrl, title }: IFrameAppShellProps) {
  const { token } = useAuth();
  const { activeEntity } = useEntity();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [isIframeLoaded, setIsIframeLoaded] = useState(false);
  const loadedIframeUrlRef = useRef<string | null>(null);

  // Extract the origin from the iframe URL for postMessage security
  // postMessage requires a target origin to prevent XSS attacks
  const targetOrigin = useMemo(() => {
    if (!iframeUrl) return null;
    try {
      return new URL(iframeUrl).origin;
    } catch {
      return null;
    }
  }, [iframeUrl]);

  // Build the iframe src URL with the embedded=1 query parameter
  const iframeSrc = useMemo(() => {
    if (!iframeUrl) return '';
    try {
      const url = new URL(iframeUrl);
      url.searchParams.set('embedded', '1');
      return url.toString();
    } catch {
      // Fallback to string concatenation if URL is malformed
      return `${iframeUrl}${iframeUrl.includes('?') ? '&' : '?'}embedded=1`;
    }
  }, [iframeUrl]);

  // Reset loading state when iframe URL changes
  // This prevents stale state when navigating between different iframe apps
  useEffect(() => {
    setIsIframeLoaded(false);
    loadedIframeUrlRef.current = null;
  }, [iframeUrl]);

  /**
   * Handles the iframe load event.
   * Only marks the iframe as loaded if it matches the current iframeUrl.
   * This prevents race conditions when navigating between different iframes.
   */
  const handleIframeLoad = () => {
    const iframe = iframeRef.current;
    if (!iframe || !iframeUrl) return;

    // Compare the same serialized URL assigned to src: URL normalizes the host/port,
    // and embedded=1 is inserted before a fragment, so the raw input may not match.
    if (iframe.src === iframeSrc) {
      setIsIframeLoaded(true);
      loadedIframeUrlRef.current = iframeUrl;
    }
  };

  /**
   * Posts auth and entity context to the iframe as separate postMessage payloads.
   * Only executes when:
   * 1. The iframe has loaded
   * 2. We have a valid token and active entity UUID
   * 3. The loaded iframe matches the current iframeUrl
   *
   * Auth and entity are sent sequentially so consumers can listen for each type
   * independently and combine them (mirrors RemoteAppShell's auth0Config + activeEntity).
   */
  useEffect(() => {
    const canPostMessage =
      isIframeLoaded &&
      token &&
      activeEntity?.id &&
      targetOrigin &&
      iframeRef.current &&
      loadedIframeUrlRef.current === iframeUrl;

    if (!canPostMessage) {
      if (isIframeLoaded && loadedIframeUrlRef.current !== iframeUrl) {
        console.log(
          `[IFrameAppShell] Skipping postMessage - loaded iframe (${loadedIframeUrlRef.current}) doesn't match current URL (${iframeUrl})`
        );
      }
      return;
    }

    const contentWindow = iframeRef.current.contentWindow;
    contentWindow?.postMessage({ type: 'iframe-auth-token', token }, targetOrigin);
    contentWindow?.postMessage(
      { type: 'iframe-entity-context', entityUUID: activeEntity.id },
      targetOrigin
    );
  }, [token, activeEntity?.id, isIframeLoaded, targetOrigin, iframeUrl]);

  if (!iframeUrl) {
    return null;
  }

  return (
    <Box
      component="iframe"
      title={title}
      ref={iframeRef}
      onLoad={handleIframeLoad}
      src={iframeSrc}
      allow="fullscreen"
      sx={{ width: '100%', height: '100%', border: 0 }}
    />
  );
}
